import { useCallback, useEffect, useRef, useState } from 'react'
import toast from 'react-hot-toast'
import {
  adminApi,
  type ExportJob,
  type SessionExportPayload,
} from '../lib/api/admin'
import { friendlyApiError } from '../lib/utils/apiError'
import { downloadBlob } from '../lib/utils/download'

/**
 * Runs a scheduling-data export end to end.
 *
 * A caller just says "export as xlsx with these filters" and gets a file. This
 * hook hides the fact that the backend answers in one of two ways:
 *
 *  - **200 + file** for a small result set → downloaded immediately.
 *  - **202 + job id** for a large one → polled until it finishes, then
 *    downloaded automatically.
 *
 * Polling starts at 2s and eases to 5s so a long job does not hammer the API,
 * and gives up after 10 minutes rather than looping forever. Timers are cleared
 * on unmount, so navigating away mid-export does not leak an interval or fire a
 * toast against an unmounted tree.
 */

const POLL_START_MS = 2000
const POLL_MAX_MS = 5000
const POLL_BACKOFF_AFTER = 5 // polls at the fast interval before easing off
const POLL_TIMEOUT_MS = 10 * 60 * 1000

export interface UseSessionExportResult {
  /** Kick off an export. Resolves when the file has downloaded (or failed). */
  runExport: (payload: SessionExportPayload) => Promise<void>
  /** True while rendering inline, queuing, or polling a background job. */
  exporting: boolean
  /** The background job currently being polled, if any. */
  activeJob: ExportJob | null
  /** Stop polling and clear state (does not cancel the server-side job). */
  reset: () => void
}

export function useSessionExport(): UseSessionExportResult {
  const [exporting, setExporting] = useState(false)
  const [activeJob, setActiveJob] = useState<ExportJob | null>(null)

  // Refs so cleanup can reach the live timer without re-running the effect.
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const cancelledRef = useRef(false)

  const clearTimer = useCallback(() => {
    if (timerRef.current !== null) {
      clearTimeout(timerRef.current)
      timerRef.current = null
    }
  }, [])

  useEffect(
    () => () => {
      cancelledRef.current = true
      clearTimer()
    },
    [clearTimer],
  )

  const reset = useCallback(() => {
    clearTimer()
    setActiveJob(null)
    setExporting(false)
  }, [clearTimer])

  /** Poll one job to a terminal state, then download it. */
  const pollUntilReady = useCallback(
    (jobId: string, toastId: string) =>
      new Promise<void>((resolve) => {
        const startedAt = Date.now()
        let polls = 0

        const finish = (fn: () => void) => {
          clearTimer()
          setActiveJob(null)
          fn()
          resolve()
        }

        const tick = async () => {
          if (cancelledRef.current) return resolve()

          if (Date.now() - startedAt > POLL_TIMEOUT_MS) {
            return finish(() =>
              toast.error(
                'The export is taking unusually long. Check "Recent exports" in a ' +
                  'few minutes — it will still be there when it finishes.',
                { id: toastId },
              ),
            )
          }

          let job: ExportJob
          try {
            job = await adminApi.getExportJob(jobId)
          } catch (err) {
            return finish(() =>
              toast.error(friendlyApiError(err, 'Lost track of the export job.'), {
                id: toastId,
              }),
            )
          }
          if (cancelledRef.current) return resolve()
          setActiveJob(job)

          if (job.status === 'failed') {
            return finish(() =>
              toast.error(job.error_message || 'The export failed to generate.', {
                id: toastId,
              }),
            )
          }

          if (job.status === 'success') {
            try {
              const { blob, filename } = await adminApi.downloadExportJob(
                jobId,
                job.file_name || `sessions.${job.export_format}`,
              )
              if (cancelledRef.current) return resolve()
              downloadBlob(blob, filename)
              return finish(() =>
                toast.success(
                  `Export ready — ${(job.row_count ?? 0).toLocaleString()} rows ` +
                    `downloaded${job.truncated ? ' (truncated)' : ''}.`,
                  { id: toastId },
                ),
              )
            } catch (err) {
              return finish(() =>
                toast.error(friendlyApiError(err, 'Could not download the export.'), {
                  id: toastId,
                }),
              )
            }
          }

          // Still queued/running — schedule the next check, easing the interval.
          polls += 1
          const delay = polls > POLL_BACKOFF_AFTER ? POLL_MAX_MS : POLL_START_MS
          toast.loading(
            job.status === 'running'
              ? 'Generating your export…'
              : 'Export queued, waiting for a worker…',
            { id: toastId },
          )
          timerRef.current = setTimeout(tick, delay)
        }

        timerRef.current = setTimeout(tick, POLL_START_MS)
      }),
    [clearTimer],
  )

  const runExport = useCallback(
    async (payload: SessionExportPayload) => {
      cancelledRef.current = false
      setExporting(true)
      const toastId = toast.loading('Preparing your export…')

      try {
        const result = await adminApi.createSessionExport(payload)

        if (result.kind === 'file') {
          downloadBlob(result.blob, result.filename)
          toast.success('Export downloaded.', { id: toastId })
          return
        }

        // Large result set: the worker is building it.
        toast.loading(result.job.message, { id: toastId })
        await pollUntilReady(result.job.job_id, toastId)
      } catch (err) {
        // Covers the row-cap 422 whose message tells the admin how to proceed.
        toast.error(friendlyApiError(err, 'Failed to export sessions.'), {
          id: toastId,
        })
      } finally {
        if (!cancelledRef.current) setExporting(false)
      }
    },
    [pollUntilReady],
  )

  return { runExport, exporting, activeJob, reset }
}
